import { Button, Col, Form, Row, Spin } from 'antd';
import { FormAttachment, FormSelect } from '../../../components/form/FormBasic';
import { FileTypeEnum } from '../../../enums/FileTypeEnum';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getExternalDataUploadTypeOptions } from '../../../enums/ExternalDataUploadTypeEnum';
import { useTranslation } from 'react-i18next';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import {
  useDownloadExternalDataTemplate,
  useUploadExternalData,
} from '../../../api/hooks/useExternalDataQuery';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useWatch } from 'antd/es/form/Form';
import { Download, Upload } from 'react-bootstrap-icons';
import { UploadResultSummary } from './UploadResultSummary';

interface DataUploadSectionProps {
  activeTab: string;
}
export const DataUploadSection: React.FC<DataUploadSectionProps> = ({ activeTab }) => {
  const { t } = useTranslation(['dataUpload']);
  const [externalDataUploadForm] = Form.useForm();
  const summaryRef = useRef<HTMLDivElement>(null);

  const uploadMutation = useUploadExternalData();
  const downloadTemplateMutation = useDownloadExternalDataTemplate();
  const { successNotification, errorNotification } = useNotificationContext();

  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const attachmentValue = useWatch('fileContent', externalDataUploadForm);
  const datasetType = useWatch('externalDataUploadType', externalDataUploadForm);

  const externalDataUploadTypeOptions = useMemo(() => getExternalDataUploadTypeOptions(t), [t]);

  const hasAllValues = useMemo(() => {
    return !!datasetType && attachmentValue && attachmentValue?.length > 0;
  }, [datasetType, attachmentValue]);

  useEffect(() => {
    if (activeTab !== '6') {
      setUploadResponse(null);
      externalDataUploadForm.resetFields();
    }
  }, [activeTab]);

  useEffect(() => {
    if (uploadResponse && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [uploadResponse]);

  const handleFormChanges = () => {
    if (uploadResponse) {
      setUploadResponse(null);
    }
  };

  const handleSubmit = (values: any) => {
    const fileBase64 = values.fileContent?.[0]?.image;

    if (!fileBase64) {
      return;
    }

    setUploadResponse(null);

    uploadMutation.mutate(
      {
        fileContent: fileBase64,
        type: values?.externalDataUploadType,
      },
      {
        onSuccess: (response: any) => {
          setUploadResponse(response?.data?.data);

          const successMessage = response?.data?.message || t('uploadSuccess');
          successNotification(successMessage);

          externalDataUploadForm.resetFields();
        },
        onError: (error: any) => {
          setUploadResponse(error);
          errorNotification(error?.message);
          externalDataUploadForm.resetFields();
        },
      }
    );
  };

  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate(
      { type: datasetType },
      {
        onSuccess: (response: any) => {
          const blob = response instanceof Blob ? response : response?.data || response;

          if (!(blob instanceof Blob)) {
            errorNotification(t('templateDownloadError'));
            return;
          }
          let fileName = 'externalDatatemplate.xlsx';
          const contentDisposition = response?.response?.headers?.['content-disposition'];

          if(contentDisposition){
            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
          successNotification(t('templateDownloadReady'));
        },
        onError: (_error: any) => {
          errorNotification(t('templateDownloadError'));
        },
      }
    );
  };

  return (
    <div className="mb-6">
      <div className="mb-2 items-start">
        <h3 className="text-2xl font-semibold">{t('BCHPricing')}</h3>
      </div>

      <Form
        form={externalDataUploadForm}
        onFinish={handleSubmit}
        layout="vertical"
        className="w-full"
        onValuesChange={handleFormChanges}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} lg={8} xl={8}>
            <FormSelect
              name={'externalDataUploadType'}
              label={t('externalDataUploadType')}
              placeholder={t('externalDataUploadTypePlaceholder')}
              rules={[{ required: true, message: t('externalDataUploadTypeRequired') }]}
              options={externalDataUploadTypeOptions}
            />
          </Col>
        </Row>

        {datasetType && (
          <Row className="mb-4">
            <Col span={24}>
              <Button
                type="link"
                size="small"
                icon={<Download size={16} />}
                onClick={handleDownloadTemplate}
                loading={downloadTemplateMutation.isPending}
                className="p-0"
              >
                {t('downloadTemplate')}
              </Button>
            </Col>
          </Row>
        )}

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FormAttachment
              name={'fileContent'}
              label={t('uploadAttachment')}
              form={externalDataUploadForm}
              isRequired
              validationMessage={t('attachmentRequired')}
              maxCount={1}
              fileTypes={[FileTypeEnum.EXCEL]}
              initialFiles={attachmentValue ? attachmentValue : []}
            />
          </Col>
        </Row>

        <Row className="mt-6 justify-end gap-4">
          <Form.Item className="flex justify-end">
            <PrimarySquareBtn
              handleOnClick={() => {}}
              disabled={uploadMutation.isPending || !hasAllValues}
            >
              <div className="flex">
                {/* <Upload className="h-4 w-6 text-white" /> */}
                <p className="ml-1 pt-0">{t('upload')}</p>
              </div>
            </PrimarySquareBtn>
          </Form.Item>
        </Row>
      </Form>

      <div ref={summaryRef} className='mb-6'> 
        <Spin spinning={uploadMutation.isPending} tip={t('processingData')} size="large">
          <UploadResultSummary response={uploadResponse} />
        </Spin>
      </div>
    </div>
  );
};
